import type { CartItem } from '$lib/types/cart';
import type { Product } from '$lib/types/product';
import { browser } from '$app/environment';
import { goto } from '$app/navigation';

interface CartState {
	items: CartItem[];
	loading: boolean;
	error: string | null;
	lastSyncedAt: Date | null;
	pendingOperations: Map<string, Promise<unknown>>;
}

interface CartUpdateEvent {
	type: 'add' | 'update' | 'remove' | 'clear' | 'sync';
	productId?: number;
	quantity?: number;
	timestamp: Date;
}

// Enhanced Cart Store with persistence and optimistic updates
class EnhancedCartStore {
	private state = $state<CartState>({
		items: [],
		loading: false,
		error: null,
		lastSyncedAt: null,
		pendingOperations: new Map(),
	});

	private marketId: number | null = null;
	private userId: number | null = null;
	private syncDebounceTimer: NodeJS.Timeout | null = null;
	private updateQueue: CartUpdateEvent[] = [];

	constructor() {
		// Load from localStorage on initialization
		if (browser) {
			this.loadFromLocalStorage();
			// Sync with server on load
			this.syncWithServer();
		}
	}

	// Getters
	get items() {
		return this.state.items;
	}

	get loading() {
		return this.state.loading;
	}

	get error() {
		return this.state.error;
	}

	get itemCount() {
		return this.state.items.reduce((count, item) => {
			const qty = Number(item.quantity) || 0;
			return count + qty;
		}, 0);
	}

	get subtotal() {
		return this.state.items.reduce((total, item) => {
			const price = Number(item.product?.appliedPrice ?? item.price ?? item.product?.price ?? 0);
			const qty = Number(item.quantity) || 0;
			return total + price * qty;
		}, 0);
	}

	// Initialize store with user/market context
	initialize(userId: number | null, marketId: number | null) {
		this.userId = userId;
		this.marketId = marketId;

		if (userId && marketId) {
			this.syncWithServer();
		}
	}

	// Add product to cart with optimistic update
	async addToCart(product: Product, quantity: number = 1) {
		if (!this.marketId || !product.id) return;

		// Generate unique temporary ID
		// Use negative numbers for temporary IDs to distinguish from server IDs
		const temporaryId = -Math.floor(Math.random() * 1000000000) - Date.now();

		// Use appliedPrice if available (includes surcharge), fallback to price
		const itemPrice = product.appliedPrice ?? product.price;

		// Optimistic update
		const optimisticItem: CartItem = {
			id: temporaryId,
			productId: product.id,
			quantity,
			product,
			productName: product.name,
			unit: product.unit,
			price: itemPrice,
			total: itemPrice * quantity,
		};

		const existingIndex = this.state.items.findIndex((item) => item.productId === product.id);

		if (existingIndex >= 0) {
			// Update existing item
			this.state.items[existingIndex].quantity += quantity;
			// Update price to latest appliedPrice (in case it changed)
			this.state.items[existingIndex].price = itemPrice;
			// Recalculate total with updated price
			this.state.items[existingIndex].total = itemPrice * this.state.items[existingIndex].quantity;
		} else {
			// Add new item
			this.state.items = [...this.state.items, optimisticItem];
		}

		// Queue update event
		this.queueUpdate({
			type: 'add',
			productId: product.id,
			quantity,
			timestamp: new Date(),
		});

		// Persist to localStorage
		this.saveToLocalStorage();

		// Sync with server (debounced)
		this.debouncedSync();
	}

	// Update item quantity with optimistic update
	async updateQuantity(productId: number, quantity: number) {
		const itemIndex = this.state.items.findIndex((item) => item.productId === productId);

		if (itemIndex < 0) return;

		// Optimistic update
		if (quantity <= 0) {
			// Remove item
			this.state.items = this.state.items.filter((item) => item.productId !== productId);
		} else {
			// Update quantity
			this.state.items[itemIndex].quantity = quantity;
			// Use appliedPrice if available, fallback to price
			const itemPrice =
				this.state.items[itemIndex].product?.appliedPrice ??
				this.state.items[itemIndex].price ??
				this.state.items[itemIndex].product?.price ??
				0;
			// Recalculate total with correct price
			this.state.items[itemIndex].total = itemPrice * quantity;
		}

		// Queue update event
		this.queueUpdate({
			type: quantity <= 0 ? 'remove' : 'update',
			productId,
			quantity,
			timestamp: new Date(),
		});

		// Persist to localStorage
		this.saveToLocalStorage();

		// Sync with server (debounced)
		this.debouncedSync();
	}

	// Remove item from cart
	async removeFromCart(productId: number) {
		await this.updateQuantity(productId, 0);
	}

	// Clear entire cart
	async clearCart() {
		// Optimistic update
		this.state.items = [];

		// Queue update event
		this.queueUpdate({
			type: 'clear',
			timestamp: new Date(),
		});

		// Clear localStorage
		if (browser) {
			localStorage.removeItem(this.getStorageKey());
		}

		// Sync with server
		if (this.userId && this.marketId) {
			try {
				const response = await fetch('/api/cart/clear', {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
				});

				if (!response.ok) {
					throw new Error('Failed to clear cart');
				}
			} catch (error) {
				console.error('Error clearing cart:', error);
				this.state.error = 'Failed to clear cart';
				// Restore from server on error
				await this.syncWithServer();
			}
		}
	}

	// Load cart from server (alias for syncWithServer)
	async loadFromServer() {
		return this.syncWithServer();
	}

	// Sync cart with server
	async syncWithServer() {
		if (!this.userId || !this.marketId) {
			return;
		}

		this.state.loading = true;
		this.state.error = null;

		try {
			const response = await fetch('/api/cart');
			if (!response.ok) {
				throw new Error('Failed to fetch cart');
			}

			const serverCart = await response.json();

			// Handle different response formats from the API
			let cartItems = [];
			if (serverCart.success && serverCart.data) {
				// Format: {success: true, data: [...]}
				cartItems = serverCart.data;
			} else if (serverCart.items) {
				// Format: {items: [...]}
				cartItems = serverCart.items;
			} else if (Array.isArray(serverCart)) {
				// Format: direct array
				cartItems = serverCart;
			}

			// Transform server format to frontend format
			cartItems = cartItems.map((item: unknown) => {
				const typedItem = item as Record<string, unknown>;
				// If item has cartItem and product properties, flatten them
				if (typedItem.cartItem && typedItem.product) {
					const cartItem = typedItem.cartItem as Record<string, unknown>;
					const product = typedItem.product as Record<string, unknown>;
					// Use appliedPrice if available, fallback to price
					const itemPrice = Number(product.appliedPrice ?? product.price ?? 0);
					return {
						id: cartItem.id,
						productId: cartItem.productId,
						quantity: cartItem.quantity,
						product: typedItem.product,
						productName: product.name,
						unit: product.unit,
						price: itemPrice,
						total: itemPrice * Number(cartItem.quantity),
					};
				}
				// Otherwise assume it's already in the right format
				// But still ensure we use appliedPrice if available
				const product = typedItem.product as Record<string, unknown> | undefined;
				if (product && product.appliedPrice !== undefined) {
					const itemPrice = Number(product.appliedPrice);
					return {
						...typedItem,
						price: itemPrice,
						total: itemPrice * Number(typedItem.quantity ?? 0),
					} as unknown as CartItem;
				}
				return typedItem as unknown as CartItem;
			});

			// Merge with local changes if needed
			if (this.updateQueue.length > 0) {
				// Apply pending updates to server cart
				const mergedCart = this.mergeCartStates(cartItems, this.updateQueue);
				this.state.items = mergedCart;
			} else {
				this.state.items = cartItems;
			}

			this.state.lastSyncedAt = new Date();
			this.updateQueue = [];

			// Save to localStorage
			this.saveToLocalStorage();
		} catch (error) {
			console.error('Error syncing cart:', error);
			this.state.error = 'Failed to sync cart with server';
		} finally {
			this.state.loading = false;
		}
	}

	// Process queued updates
	private async processUpdateQueue() {
		if (this.updateQueue.length === 0 || !this.userId || !this.marketId) {
			return;
		}

		const updates = [...this.updateQueue];
		this.updateQueue = [];

		try {
			const response = await fetch('/api/cart/batch-update', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ updates }),
			});

			if (!response.ok) {
				throw new Error('Failed to process updates');
			}

			const result = await response.json();

			// Handle different response formats
			let items = [];
			if (result.success && result.data) {
				items = result.data;
			} else if (result.items) {
				items = result.items;
			} else if (Array.isArray(result)) {
				items = result;
			}

			// Transform server format to frontend format
			if (items && items.length > 0) {
				this.state.items = items.map((item: unknown) => {
					const typedItem = item as Record<string, unknown>;
					// If item has cartItem and product properties, flatten them
					if (typedItem.cartItem && typedItem.product) {
						const cartItem = typedItem.cartItem as Record<string, unknown>;
						const product = typedItem.product as Record<string, unknown>;
						// Use appliedPrice if available, fallback to price
						const itemPrice = Number(product.appliedPrice ?? product.price ?? 0);
						return {
							id: cartItem.id,
							productId: cartItem.productId,
							quantity: cartItem.quantity,
							product: typedItem.product,
							productName: product.name,
							unit: product.unit,
							price: itemPrice,
							total: itemPrice * Number(cartItem.quantity),
						};
					}
					// Otherwise assume it's already in the right format
					// But still ensure we use appliedPrice if available
					const product = typedItem.product as Record<string, unknown> | undefined;
					if (product && product.appliedPrice !== undefined) {
						const itemPrice = Number(product.appliedPrice);
						return {
							...typedItem,
							price: itemPrice,
							total: itemPrice * Number(typedItem.quantity ?? 0),
						} as unknown as CartItem;
					}
					return typedItem as unknown as CartItem;
				});
			}

			this.state.lastSyncedAt = new Date();
		} catch (error) {
			console.error('Error processing updates:', error);
			// Re-queue failed updates
			this.updateQueue = [...updates, ...this.updateQueue];
			throw error;
		}
	}

	// Queue an update event
	private queueUpdate(event: CartUpdateEvent) {
		this.updateQueue.push(event);
	}

	// Debounced sync
	private debouncedSync() {
		if (this.syncDebounceTimer) {
			clearTimeout(this.syncDebounceTimer);
		}

		this.syncDebounceTimer = setTimeout(() => {
			this.processUpdateQueue();
		}, 500);
	}

	// Merge server cart with local updates
	private mergeCartStates(serverItems: CartItem[], updates: CartUpdateEvent[]): CartItem[] {
		let merged = [...serverItems];

		for (const update of updates) {
			switch (update.type) {
				case 'add':
				case 'update':
					if (update.productId && update.quantity !== undefined) {
						const index = merged.findIndex((item) => item.productId === update.productId);
						if (index >= 0) {
							merged[index].quantity = update.quantity;
						}
					}
					break;
				case 'remove':
					if (update.productId) {
						merged = merged.filter((item) => item.productId !== update.productId);
					}
					break;
				case 'clear':
					merged = [];
					break;
			}
		}

		return merged;
	}

	// Local storage helpers
	private getStorageKey(): string {
		return `cart_${this.marketId}_${this.userId || 'guest'}`;
	}

	private saveToLocalStorage() {
		if (!browser) return;

		const data = {
			items: this.state.items,
			lastSyncedAt: this.state.lastSyncedAt,
			updateQueue: this.updateQueue,
		};

		localStorage.setItem(this.getStorageKey(), JSON.stringify(data));
	}

	private loadFromLocalStorage() {
		if (!browser) return;

		try {
			const key = this.getStorageKey();
			const stored = localStorage.getItem(key);
			if (stored) {
				const data = JSON.parse(stored);
				this.state.items = data.items || [];
				this.state.lastSyncedAt = data.lastSyncedAt ? new Date(data.lastSyncedAt) : null;
				this.updateQueue = data.updateQueue || [];
			}
		} catch (error) {
			console.error('Error loading cart from localStorage:', error);
		}
	}

	// Checkout helper
	async proceedToCheckout() {
		if (this.state.items.length === 0) {
			this.state.error = 'Your cart is empty';
			return;
		}

		// Ensure all updates are synced
		if (this.updateQueue.length > 0) {
			this.state.loading = true;
			try {
				await this.processUpdateQueue();
			} catch {
				this.state.error = 'Failed to sync cart. Please try again.';
				return;
			} finally {
				this.state.loading = false;
			}
		}

		// Navigate to checkout
		goto('/modern/cart/review');
	}

	// Utility methods
	getItemByProductId(productId: number): CartItem | undefined {
		return this.state.items.find((item) => item.productId === productId);
	}

	hasProduct(productId: number): boolean {
		return this.state.items.some((item) => item.productId === productId);
	}

	getProductQuantity(productId: number): number {
		const item = this.getItemByProductId(productId);
		return item?.quantity || 0;
	}
}

// Create and export enhanced cart store instance
export const enhancedCartStore = new EnhancedCartStore();

// Keep original cart store exports for backward compatibility
export const cartStore = enhancedCartStore;

// Export utility functions
export {
	getCartTotal,
	getCartSurcharge,
	getCartMembershipFee,
	getCartFinalTotal,
} from './cart.svelte';

// Additional cart-related helpers
export function formatCartItemsForCheckout(items: CartItem[]) {
	return items.map((item) => ({
		productId: item.productId,
		quantity: item.quantity,
		price: item.product?.price || '0',
		name: item.product?.name || 'Unknown Product',
		growerName: item.product?.growerName || 'Unknown Grower',
		unit: item.product?.unit || '',
	}));
}

export function validateCartItems(items: CartItem[]): {
	valid: boolean;
	errors: string[];
} {
	const errors: string[] = [];

	for (const item of items) {
		if (!item.product) {
			errors.push(`Invalid product in cart`);
			continue;
		}

		if (!item.product.isActive) {
			errors.push(`${item.product.name} is no longer available`);
		}

		if (item.quantity > (item.product.quantity || 0)) {
			errors.push(
				`Only ${item.product.quantity} ${item.product.unit || 'units'} of ${item.product.name} available`
			);
		}
	}

	return {
		valid: errors.length === 0,
		errors,
	};
}
